import type { ChatTheme } from '@kairelo/shared';

/**
 * On-device translation adapter (concept §8).
 *
 * The V1 rule, stated exactly: translation runs **locally on the participant's
 * device**, for **written chat messages only**, and **no external translation
 * provider receives message text**.
 *
 * This adapter therefore has no network code path at all. There is no API key,
 * no endpoint and no fetch. If the browser cannot translate locally, the
 * adapter reports `unavailable` and the interface says so — it does not quietly
 * fall back to a server.
 *
 * Three availability states, and the product distinguishes them everywhere:
 *
 *   browser      — genuine on-device translation provided by the platform.
 *   mock         — the deterministic test adapter. Only ever reachable when the
 *                  build flag VITE_TEST_TRANSLATOR_ENABLED is on, and the UI
 *                  always labels it as a test adapter so it can never be
 *                  mistaken for real support.
 *   unavailable  — no local translation for this browser or language pair.
 */

export type TranslationAvailability = 'browser' | 'mock' | 'unavailable';

export interface TranslationCapability {
  availability: TranslationAvailability;
  /** Shown verbatim in the UI when translation cannot be offered. */
  detail: string;
  /** True only for a real platform translator. Never true for the mock. */
  isGenuineOnDevice: boolean;
}

export interface Translator {
  translate(text: string): Promise<string>;
  readonly sourceLanguage: string;
  readonly targetLanguage: string;
  readonly availability: TranslationAvailability;
  destroy(): void;
}

/**
 * The platform on-device translation API, as exposed by browsers that ship it.
 * Typed structurally so the build does not depend on a specific vendor's
 * TypeScript definitions.
 */
interface PlatformTranslatorFactory {
  availability?(options: {
    sourceLanguage: string;
    targetLanguage: string;
  }): Promise<'unavailable' | 'downloadable' | 'downloading' | 'available'>;
  create(options: {
    sourceLanguage: string;
    targetLanguage: string;
    monitor?: (monitor: EventTarget) => void;
  }): Promise<{ translate(input: string): Promise<string>; destroy?(): void }>;
}

function platformTranslator(): PlatformTranslatorFactory | null {
  const candidate = (globalThis as Record<string, unknown>).Translator;
  if (candidate && typeof (candidate as PlatformTranslatorFactory).create === 'function') {
    return candidate as PlatformTranslatorFactory;
  }
  return null;
}

function mockEnabled(): boolean {
  return import.meta.env.VITE_TEST_TRANSLATOR_ENABLED === 'true';
}

/**
 * Deterministic test adapter.
 *
 * Exists so the end-to-end suite can assert the translated and untranslated
 * states without depending on a browser feature that headless Chromium does not
 * ship. It is not translation, it never pretends to be, and every surface that
 * uses it is labelled.
 */
function createMockTranslator(sourceLanguage: string, targetLanguage: string): Translator {
  return {
    sourceLanguage,
    targetLanguage,
    availability: 'mock',
    async translate(text: string) {
      return `[${targetLanguage}] ${text}`;
    },
    destroy() {},
  };
}

/** Reports whether this environment can translate locally, without creating anything. */
export async function detectCapability(params: {
  sourceLanguage: string;
  targetLanguage: string;
}): Promise<TranslationCapability> {
  if (params.sourceLanguage === params.targetLanguage) {
    return {
      availability: 'unavailable',
      detail: 'Both languages are the same, so nothing needs translating.',
      isGenuineOnDevice: false,
    };
  }

  const factory = platformTranslator();
  if (factory) {
    try {
      const state = factory.availability
        ? await factory.availability({
            sourceLanguage: params.sourceLanguage,
            targetLanguage: params.targetLanguage,
          })
        : 'available';

      if (state === 'available' || state === 'downloadable' || state === 'downloading') {
        return {
          availability: 'browser',
          detail:
            state === 'available'
              ? 'This browser can translate written messages on your device.'
              : 'This browser will download a small local language pack the first time you translate.',
          isGenuineOnDevice: true,
        };
      }
    } catch {
      // Fall through to the unavailable state below.
    }
  }

  if (mockEnabled()) {
    return {
      availability: 'mock',
      detail:
        'Test translation adapter. This is not real translation and is only enabled in test builds.',
      isGenuineOnDevice: false,
    };
  }

  return {
    availability: 'unavailable',
    detail:
      'Translation is not available in this browser for this language pair. ' +
      'Kairelo only translates on your own device and never sends messages to a translation service, ' +
      'so the conversation continues without it.',
    isGenuineOnDevice: false,
  };
}

/** Creates a translator, or null when local translation is unavailable. */
export async function createTranslator(params: {
  sourceLanguage: string;
  targetLanguage: string;
}): Promise<Translator | null> {
  const capability = await detectCapability(params);

  if (capability.availability === 'browser') {
    const factory = platformTranslator();
    if (!factory) return null;
    const instance = await factory.create({
      sourceLanguage: params.sourceLanguage,
      targetLanguage: params.targetLanguage,
    });
    return {
      sourceLanguage: params.sourceLanguage,
      targetLanguage: params.targetLanguage,
      availability: 'browser',
      translate: (text) => instance.translate(text),
      destroy: () => instance.destroy?.(),
    };
  }

  if (capability.availability === 'mock') {
    return createMockTranslator(params.sourceLanguage, params.targetLanguage);
  }

  return null;
}

/** The viewer's own language, used as the default translation target. */
export function preferredLanguage(): string {
  const tag = typeof navigator !== 'undefined' ? navigator.language : 'en';
  return (tag || 'en').split('-')[0] ?? 'en';
}

/** Voice, files, documents and video are never translated in V1 (concept §8). */
export function isTranslatable(kind: string): boolean {
  return kind === 'text';
}

export type { ChatTheme };
